import { ObjectTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { PropertyDataSensitivityType } from 'customer-data-objects/property/PropertyDataSensitivity';
import Value from 'customer-data-objects/property/Value';
import { PromiseClient } from 'hub-http/adapters/promiseClient';
import Raven from 'raven-js';
import { incrementAutosaveEmptyParsedUpdatesMetric } from '../metrics/autosaveMetrics';
import { trackRequest } from '../metrics/requestMetrics';

export type PropertyValueMetadata = {
  value: Value;
  sourceId: string | null;
  sensitivityLevel: PropertyDataSensitivityType;
  timestamp?: number | null;
};
export type PropertyValuesWithMetadata = {
  [propertyName: string]: PropertyValueMetadata;
};

// Not a complete type, but supplies us with the fields we need.
export type CrmObject = {
  objectTypeId: string;
  objectId: number;
  properties: {
    [propertyName: string]: {
      isEncrypted?: boolean;
      sensitivityLevel: PropertyDataSensitivityType;
      value: Value;
      sourceId: string | null;
      timestamp?: number | null;
    };
  };
};

export type PropertyUpdatesById = {
  [objectId: string]: {
    [propertyName: string]: Value;
  };
};

export type PropertyUpdatesByIdWithMetadata = {
  [objectId: string]: PropertyValuesWithMetadata;
};

export const parseUpdateResponse = (
  response: Array<CrmObject>,
  requestedUpdates: PropertyUpdatesById
): PropertyUpdatesByIdWithMetadata => {
  const parsedResponse: PropertyUpdatesByIdWithMetadata = {};

  for (const { objectId, properties } of response) {
    parsedResponse[objectId] = {};
    const request = requestedUpdates[objectId];

    for (const [propertyName, update] of Object.entries(properties)) {
      let value = update.value;
      const { sourceId, sensitivityLevel, timestamp } = update;

      // Encrypted values are not returned by the endpoint, so we rely on
      // the requested value since it's our best guess at what the value should be.
      if (update.isEncrypted) {
        value = request[propertyName];
      }

      parsedResponse[objectId][propertyName] = {
        value,
        sourceId,
        sensitivityLevel,
        timestamp,
      };
    }

    if (Object.keys(parsedResponse[objectId]).length === 0) {
      incrementAutosaveEmptyParsedUpdatesMetric();
    }
  }

  return parsedResponse;
};

export const savePropertyValues = (
  objectTypeId: ObjectTypeId,
  updates: PropertyUpdatesById,
  httpClient: PromiseClient
): Promise<PropertyUpdatesByIdWithMetadata> => {
  // TODO: consider using the client-types package to type this data
  // import { UpdateBatchResponse } from 'inbounddb-objects-service-types/InboundDbObjects-Service-v1/types';
  // at the moment, this type is really lacking since the PropertiesWrapper type is just Record<string, unknown>
  // and the extra dep is not worth it.
  // sswindell @ 10-17-2024

  const request = trackRequest('savepropertyvalues');
  return httpClient
    .put<Array<CrmObject>>(
      `inbounddb-objects/v1/crm-objects/${encodeURIComponent(objectTypeId)}`,
      {
        data: Object.entries(updates).map(([objectId, update]) => ({
          objectId,
          propertyValues: Object.entries(update).map(([name, value]) => ({
            name,
            value,
          })),
        })),
        keepalive: true,
      }
    )
    .then((response) => {
      request.success();
      return parseUpdateResponse(response, updates);
    })
    .catch((e) => {
      request.failure(e);
      Raven.captureException(e, {
        extra: {
          objectTypeId,
          updates,
        },
      });
      throw e;
    });
};
